import { onBeforeUnmount } from 'vue';
import { Capacitor } from '@capacitor/core';
import VueCookies from 'vue-cookies';
import { useAuthStore } from '@/stores/auth';

const TAB_SET_KEY = 'bn_open_tabs';
const TAB_ID_KEY = 'bn_tab_id';
const STALE_THRESHOLD_MS = 24 * 60 * 60 * 1000;

function getTabSet() {
    try {
        const raw = localStorage.getItem(TAB_SET_KEY);
        const parsed = raw ? JSON.parse(raw) : {};
        if (Array.isArray(parsed) || typeof parsed !== 'object' || parsed === null) return {};
        return parsed;
    } catch {
        return {};
    }
}

function saveTabSet(tabs) {
    localStorage.setItem(TAB_SET_KEY, JSON.stringify(tabs));
}

function addTab(id) {
    const tabs = getTabSet();
    tabs[id] = Date.now();
    saveTabSet(tabs);
}

function removeTab(id) {
    const tabs = getTabSet();
    delete tabs[id];
    saveTabSet(tabs);
    return Object.keys(tabs).length;
}

function pruneStale() {
    const tabs = getTabSet();
    const now = Date.now();
    let changed = false;
    for (const id of Object.keys(tabs)) {
        if (now - tabs[id] > STALE_THRESHOLD_MS) {
            delete tabs[id];
            changed = true;
        }
    }
    if (changed) saveTabSet(tabs);
}

export function useLogoutOnLastTabClose() {
    if (Capacitor.getPlatform() !== 'web') return;

    pruneStale();

    // sessionStorage survives a refresh (pagehide -> reload of the same tab) but is
    // wiped when a tab actually closes. That's the only reliable way to tell the two
    // apart, since both fire the same 'pagehide' event. Duplicating a tab copies
    // sessionStorage too, but the original tab still owns that id in the tab set -
    // a stored id that's still live in the set means duplicate, not refresh.
    const storedId = sessionStorage.getItem(TAB_ID_KEY);
    const openTabs = getTabSet();
    const isRefresh = !!storedId && !(storedId in openTabs);
    const tabId = isRefresh ? storedId : Math.random().toString(36).slice(2) + Math.random().toString(36).slice(2);
    sessionStorage.setItem(TAB_ID_KEY, tabId);

    const authStore = useAuthStore();

    if (!isRefresh && Object.keys(openTabs).length === 0) {
        // Every previously tracked tab is gone and this isn't a refresh of one of them,
        // so the last tab really did close (not just reload) - finish logging out now.
        VueCookies.remove('bn_session_active');
        authStore.removeToken();
    }

    addTab(tabId);

    const handlePageHide = () => {
        removeTab(tabId);
    };

    const handlePageShow = (event) => {
        if (event.persisted) {
            addTab(tabId);
        }
    };

    window.addEventListener('pagehide', handlePageHide);
    window.addEventListener('pageshow', handlePageShow);

    onBeforeUnmount(() => {
        window.removeEventListener('pagehide', handlePageHide);
        window.removeEventListener('pageshow', handlePageShow);
        removeTab(tabId);
    });
}
